/**
 * Manager for creating and configuring Bob (Runescape cat) meshes
 * Provides simple mesh generation with PBR materials
 */

import {
  Color3,
  Mesh,
  PBRMaterial,
  type Scene,
  Texture,
  VertexData,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager.js";
import {
  type BobMaterials,
  type BobMesh,
  getBobMaterials,
  getBobMeshes,
} from "./bobData/index.js";

/**
 * Options for creating Bob mesh
 */
export interface BobOptions {
  followAppTheme?: boolean;
  scale?: number;
}

/**
 * Manager responsible for Bob mesh creation and configuration
 */
export class BobManager extends BaseManager {
  private scene: Scene | null = null;
  private instanceCounter = 0;

  /**
   * Initialize the manager (required by BaseManager)
   */
  protected async onInitialize(): Promise<void> {
    // No async initialization needed
  }

  /**
   * Dispose the manager (required by BaseManager)
   */
  protected onDispose(): void {
    this.scene = null;
  }

  /**
   * Set the scene reference for material creation
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Get the current scene
   */
  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * Create Bob mesh from stored mesh data
   */
  createBob(scene: Scene, options: BobOptions = {}): Mesh[] {
    const meshDataArray = getBobMeshes();
    const materials = getBobMaterials();
    const meshes: Mesh[] = [];

    // Generate unique instance ID for this Bob
    const instanceId = this.instanceCounter++;

    for (const meshData of meshDataArray) {
      const mesh = this.createMeshFromData(
        meshData,
        scene,
        options,
        materials,
        instanceId,
      );
      meshes.push(mesh);
    }

    // Emit creation event
    this.eventBus.emit("bob:created", { meshCount: meshes.length });

    return meshes;
  }

  /**
   * Create a single mesh from mesh data
   */
  private createMeshFromData(
    meshData: BobMesh,
    scene: Scene,
    options: BobOptions,
    materials: BobMaterials,
    instanceId: number,
  ): Mesh {
    // Use unique name to ensure each Bob has its own physics body
    const uniqueName = `${meshData.name}_${instanceId}`;
    const mesh = new Mesh(uniqueName, scene);

    // Apply vertex data
    const vertexData = new VertexData();
    vertexData.positions = Array.from(meshData.positions);
    vertexData.indices = Array.from(meshData.indices);
    vertexData.normals = Array.from(meshData.normals);
    if (meshData.uvs) {
      vertexData.uvs = Array.from(meshData.uvs);
    }
    vertexData.applyToMesh(mesh);

    // Apply scale
    const scale = options.scale ?? 1;
    mesh.scaling.setAll(scale);

    // Get extracted material if available
    const extractedMaterial =
      meshData.materialIndex !== undefined
        ? materials[meshData.materialIndex]
        : undefined;

    // Create material with extracted data
    const material = this.createBobMaterial(
      scene,
      uniqueName,
      extractedMaterial,
    );
    mesh.material = material;

    // Store metadata
    mesh.metadata = {
      bobOptions: options,
      type: "bob",
    };

    return mesh;
  }

  /**
   * Create PBR material for Bob
   * Uses extracted material data with texture support, falls back to hardcoded colors
   */
  private createBobMaterial(
    scene: Scene,
    meshName: string,
    extractedMaterial?: {
      albedoColor?: [number, number, number];
      albedoTexture?: string;
      metallic?: number;
      roughness?: number;
      emissiveColor?: [number, number, number];
      alpha?: number;
    },
  ): PBRMaterial {
    const material = new PBRMaterial(`bobMaterial_${meshName}`, scene);

    // Use extracted material properties when available
    if (extractedMaterial) {
      // Apply albedo color (used as tint with texture)
      if (extractedMaterial.albedoColor) {
        material.albedoColor = new Color3(
          extractedMaterial.albedoColor[0],
          extractedMaterial.albedoColor[1],
          extractedMaterial.albedoColor[2],
        );
      }

      // Apply texture if available
      if (extractedMaterial.albedoTexture) {
        material.albedoTexture = new Texture(
          extractedMaterial.albedoTexture,
          scene,
        );
      }

      // Apply PBR properties
      material.metallic = extractedMaterial.metallic ?? 0.0;
      material.roughness = extractedMaterial.roughness ?? 0.5;

      // Apply emissive if present
      if (extractedMaterial.emissiveColor) {
        material.emissiveColor = new Color3(
          extractedMaterial.emissiveColor[0],
          extractedMaterial.emissiveColor[1],
          extractedMaterial.emissiveColor[2],
        );
      } else {
        material.emissiveColor = new Color3(0.02, 0.015, 0.01);
      }
    } else {
      // Fallback: Cat fur-like material properties (no texture)
      material.albedoColor = new Color3(0.15, 0.12, 0.1); // Dark brown/black
      material.metallic = 0.0;
      material.roughness = 0.8; // Matte fur-like
      material.emissiveColor = new Color3(0.02, 0.015, 0.01);
    }

    material.environmentIntensity = 0.3;

    // Disable back-face culling to render all faces (model may have inverted normals)
    material.backFaceCulling = false;
    material.twoSidedLighting = true;

    return material;
  }

  /**
   * Apply theme colors to Bob meshes
   * For Bob, we just adjust material intensity based on dark/light mode
   */
  applyThemeColors(meshes: Mesh[], _theme: string, isDarkMode: boolean): void {
    for (const mesh of meshes) {
      const material = mesh.material as PBRMaterial;
      if (material) {
        // Adjust emissive for visibility in dark mode
        if (isDarkMode) {
          material.emissiveColor = new Color3(0.04, 0.03, 0.02);
          material.environmentIntensity = 0.4;
        } else {
          material.emissiveColor = new Color3(0.02, 0.015, 0.01);
          material.environmentIntensity = 0.3;
        }
      }
    }

    this.eventBus.emit("bob:theme-applied", { isDarkMode });
  }
}
